/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL   
 */

import React, { useState } from 'react';
import adminApi from '../../services/adminApi';

const HuggingFaceUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    adapterName: '',
    domain: 'Platform',
    sourcePath: '',
    sourceType: 'local',
    kaggleNotebookId: '',
    makePrivate: true
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const domains = [
    { value: 'WMS', label: 'WMS' },
    { value: 'iSeries', label: 'iSeries' },
    { value: 'Civil', label: 'Civil' },
    { value: 'Stitching', label: 'Stitching' },
    { value: 'Platform', label: 'Platform' }
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0.00 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      if (formData.sourceType === 'local') {
        // NEW: Direct file upload using FormData
        if (selectedFiles.length === 0) {
          throw new Error('Please select adapter files to upload');
        }

        const uploadData = new FormData();
        uploadData.append('adapterName', formData.adapterName);
        uploadData.append('domain', formData.domain);
        uploadData.append('makePrivate', formData.makePrivate);
        
        // Append all selected files
        selectedFiles.forEach((file) => {
          uploadData.append('files', file);
        });

        await adminApi.post('/api/admin/adapters/upload-files-to-hf', uploadData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 600000, // 10 minutes for large file uploads (640MB+)
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        });
      } else {
        // Kaggle notebook upload (existing functionality)
        await adminApi.post('/api/admin/adapters/upload-files-to-hf', {
          adapterName: formData.adapterName,
          domain: formData.domain,
          sourceType: 'kaggle',
          kaggleNotebookId: formData.kaggleNotebookId,
          makePrivate: formData.makePrivate
        }, {
          timeout: 300000 // 5 minutes for Kaggle download + upload
        });
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear files when switching source type
    if (field === 'sourceType') {
      setSelectedFiles([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 rounded-t-xl">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            🤗 Upload to HuggingFace
          </h2>
          <p className="text-yellow-100 text-sm mt-1">
            Upload adapter files to HuggingFace repository
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Adapter Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adapter Name
            </label>
            <input
              type="text"
              value={formData.adapterName}
              onChange={(e) => handleChange('adapterName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="platform-adapter-v1"
              required
            />
          </div>

          {/* Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <select
              value={formData.domain}
              onChange={(e) => handleChange('domain', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              {domains.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Source Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="local"
                  checked={formData.sourceType === 'local'}
                  onChange={(e) => handleChange('sourceType', e.target.value)}
                  className="text-yellow-500 focus:ring-yellow-500"
                />
                <span className="text-sm">Upload from Computer</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="kaggle"
                  checked={formData.sourceType === 'kaggle'}
                  onChange={(e) => handleChange('sourceType', e.target.value)}
                  className="text-yellow-500 focus:ring-yellow-500"
                />
                <span className="text-sm">Kaggle Output</span>
              </label>
            </div>

            {formData.sourceType === 'local' ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg cursor-pointer hover:bg-yellow-200 transition-colors">
                    <span>Choose Files</span>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".safetensors,.bin,.json,.md,.txt"
                    />
                  </label>
                  <span className="text-sm text-gray-600">
                    {selectedFiles.length > 0 ? `${selectedFiles.length} files` : 'No files selected'}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Select adapter files (.safetensors, .bin, adapter_config.json, etc.)
                </p>
                
                {/* File Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Selected {selectedFiles.length} file(s):
                    </p>
                    <ul className="space-y-1">
                      {selectedFiles.map((file, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex justify-between">
                          <span className="truncate max-w-[200px]">{file.name}</span>
                          <span className="text-gray-400 ml-2">{formatFileSize(file.size)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <input
                type="text"
                value={formData.kaggleNotebookId}
                onChange={(e) => handleChange('kaggleNotebookId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="username/notebook-name"
              />
            )}
          </div>

          {/* Private Repository */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.makePrivate}
              onChange={(e) => handleChange('makePrivate', e.target.checked)}
              className="rounded text-yellow-500 focus:ring-yellow-500"
            />
            <span className="text-sm text-gray-700">Private repository</span>
          </label>

          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (formData.sourceType === 'local' && selectedFiles.length === 0)}
              className="flex-1 py-2 px-4 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Uploading...') : 'Upload to HuggingFace'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HuggingFaceUploadModal;
